'use no memo';

import { type UnifiedJobSearchResult } from '@kalos/kalos-rpc';
import {
  Button,
  cn,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { BarChartIcon, HomeIcon, Pencil1Icon, PersonIcon } from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME,
  JOB_STATUS_COLORS,
  ROWS_PER_PAGE,
} from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { useJobSubTypesQuery } from '../../../../hooks/react-query/useJobSubTypesQuery';
import { useJobTypesQuery } from '../../../../hooks/react-query/useJobTypesQuery';
import { useTimesheetDepartmentListQuery } from '../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { useBuildLocation } from '../../../../hooks/useBuildLocation';
import { useMobileDevice } from '../../../../hooks/useMedia';
import {
  debounce,
  EventClientService,
  formatDate,
  navigateNewTab,
} from '../../../../tools/helpers';
import { ExportJSON } from '../../ExportJSON';
import { downloadJsonAsCsv } from '../../ExportJSON/downloadJsonAsCsv';
import { FlatRateSheet } from '../../FlatRate';
import { Modal } from '../../Modal';
import { type AdvancedSearchProps } from '..';
import { filtersKey, useListPageAndSort } from '../listState';
import { MobileJobCard, MobileJobCardSkeleton } from './MobileJobCard';
import { UnifiedJobCreateDialog } from './UnifiedJobCreateDialog';
import { UnifiedJobSearchForm } from './UnifiedJobSearchForm';
import { UnifiedJobSearchPhone } from './UnifiedJobSearchPhone';
import {
  useUnifiedJobSearchExportQuery,
  useUnifiedJobSearchQuery,
} from './useUnifiedJobSearchQuery';
import {
  getResultRoute,
  isResultProject,
  type UnifiedJobSearchScheme,
  useUnifiedJobSearchForm,
} from './utils';

type UnifiedJobSearchProps = {
  accounting: boolean;
  showJob?: boolean;
  showExport?: boolean;
  useMobileCard?: boolean;
  disableParamsSync?: boolean;
} & Pick<AdvancedSearchProps, 'onSelectEvent' | 'onClose'>;

const loadingResults = Array.from({ length: 5 }, () => ({}) as UnifiedJobSearchResult);
const staticArray: UnifiedJobSearchResult[] = [];

const getOrderParamsFromSorting = (sorting: SortingState) => {
  const sortColumn = sorting[0];
  if (!sortColumn) {
    return {
      orderBy: 'date_started',
      orderDir: 'DESC' as const,
    };
  }

  // Map frontend column IDs to backend field names
  const columnToFieldMap: Record<string, string> = {
    date_started: 'date_started',
    log_jobNumber: 'job_number',
    job_status: 'job_status',
    department: 'department_id',
  };

  return {
    orderBy: columnToFieldMap[sortColumn.id] || sortColumn.id,
    orderDir: (sortColumn.desc ? 'DESC' : 'ASC') as 'ASC' | 'DESC',
  };
};

export const UnifiedJobSearch = ({
  accounting,
  showJob,
  onSelectEvent,
  onClose,
  showExport = false,
  useMobileCard = false,
  disableParamsSync = false,
}: UnifiedJobSearchProps) => {
  const auth = useAuth();
  const loggedUserId = auth.user.id;
  const isMobileOnly = useMobileDevice();
  const isMobile = useMobileCard || isMobileOnly;
  // Phones get their own layout (UnifiedJobSearchPhone). The dashboard's card on desktop keeps
  // the older cards (`useMobileCard`).
  const isPhone = isMobileOnly;

  const searchForm = useUnifiedJobSearchForm({ isSelector: !!onSelectEvent, disableParamsSync });

  // Page and sort live in the URL on the job search page, so a reload or a shared link keeps
  // them. A picker or the dashboard card keeps them local and never writes to its host's URL.
  const { page, setPage, sort, setSort } = useListPageAndSort({
    syncToUrl: !onSelectEvent && !disableParamsSync,
  });

  const [filters, setFilters] = useState<UnifiedJobSearchScheme>(() => searchForm.getValues());
  const sorting = useMemo<SortingState>(() => (sort ? [sort] : []), [sort]);
  const sortParams = useMemo(() => getOrderParamsFromSorting(sorting), [sorting]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [flatRateIsOpen, setFlatRateIsOpen] = useState(false);

  const userQuery = useUserQuery({
    filters: {
      id: loggedUserId || 0,
    },
    enabled: typeof loggedUserId === 'number',
    select(data) {
      return {
        ...data,
        canViewCostReport: !!data?.permissionGroups.find((el) => el.name == 'CostReportAccess'),
      };
    },
  });

  const departmentsQuery = useTimesheetDepartmentListQuery({
    filter: {
      isActive: 1,
    },
  });

  // Fetch job types and subtypes for column display
  const jobTypesQuery = useJobTypesQuery();
  const jobSubTypesQuery = useJobSubTypesQuery();

  // Create mapping from department ID to department description
  const departmentMap = useMemo(() => {
    const map = new Map<number, string>();
    if (departmentsQuery.data?.results) {
      departmentsQuery.data.results.forEach((dept) => {
        map.set(dept.id, dept.description);
      });
    }
    return map;
  }, [departmentsQuery.data?.results]);

  // Create mapping from job type ID to name
  const jobTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    if (jobTypesQuery.data) {
      jobTypesQuery.data.forEach((jt) => {
        map.set(jt.id, jt.name);
      });
    }
    return map;
  }, [jobTypesQuery.data]);

  // Create mapping from job subtype ID to name
  const jobSubTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    if (jobSubTypesQuery.data?.results) {
      jobSubTypesQuery.data.results.forEach((jst) => {
        map.set(jst.id, jst.name);
      });
    }
    return map;
  }, [jobSubTypesQuery.data?.results]);

  // Main search query
  const jobSearchQuery = useUnifiedJobSearchQuery({
    filters,
    page,
    pageSize: 25,
    orderBy: sortParams.orderBy,
    orderDir: sortParams.orderDir,
    useV2: true,
    // Phones load page after page as the list scrolls, so this paged query is the table's only.
    enabled: !isPhone,
  });

  // The last total stays up while the next page or filter loads. Unknown until the first load,
  // and the pager waits for it: it clamps the page to its page count, which would throw away a
  // page from the URL.
  const [totalEntriesCount, setTotalEntriesCount] = useState<number | null>(null);
  useEffect(() => {
    if (jobSearchQuery.isSuccess) {
      setTotalEntriesCount(jobSearchQuery.data?.totalCount || 0);
    }
  }, [jobSearchQuery.data?.totalCount, jobSearchQuery.isSuccess]);

  const buildLocation = useBuildLocation();

  const handleSelectResult = useCallback(
    async (result: UnifiedJobSearchResult) => {
      try {
        const event = await EventClientService.LoadEventByServiceCallID(result.eventId);
        if (event) {
          onSelectEvent?.(event);
          onClose?.();
        }
      } catch (err) {
        console.error('Failed to load event for selection:', err);
      }
    },
    [onSelectEvent, onClose],
  );

  const onSelectResultCallback = useCallback(
    (result: UnifiedJobSearchResult, nativeEvent?: React.MouseEvent<HTMLTableRowElement>) => {
      if (nativeEvent) {
        const target = nativeEvent.target as HTMLElement;
        const columnId = target.getAttribute('data-column-id');
        const isSelectButton = target.closest('[data-unified-job-search-select]');

        // Handle Select button click (selector mode)
        if (onSelectEvent && isSelectButton) {
          handleSelectResult(result);
          return;
        }

        // Handle customer column click
        if (!onSelectEvent && columnId && columnId.includes('customer') && result.customerId) {
          const location = buildLocation({
            to: '/customers/$customerId',
            params: {
              customerId: result.customerId,
            },
          });
          navigateNewTab(location.href);
          if (onClose) {
            onClose();
          }
          return;
        }

        // Handle property column click
        if (!onSelectEvent && columnId && columnId.includes('property')) {
          const location = buildLocation({
            to: '/properties/$propertyId',
            params: {
              propertyId: result.propertyId,
            },
          });
          navigateNewTab(location.href);
          if (onClose) {
            onClose();
          }
          return;
        }

        // Selector mode: row click (not customer/property) selects the job
        if (onSelectEvent) {
          handleSelectResult(result);
          return;
        }

        // Handle job/project navigation (non-selector mode)
        const route = getResultRoute(result);
        const location = buildLocation(route);
        if (accounting || showJob) {
          const newWindow = navigateNewTab(location.href);
          newWindow?.focus();
        }
      }

      if (onClose && !onSelectEvent) {
        onClose();
      }
    },
    [onClose, onSelectEvent, buildLocation, accounting, showJob, handleSelectResult],
  );

  // The page resets only when the filters really change, not when the form first loads from
  // the URL, so a reload keeps the page.
  const appliedFiltersKey = useRef(filtersKey(filters));
  useEffect(() => {
    const debouncedUpdate = debounce((data: UnifiedJobSearchScheme) => {
      const nextKey = filtersKey(data);
      if (nextKey === appliedFiltersKey.current) return;
      appliedFiltersKey.current = nextKey;
      setFilters(data);
      setPage(0);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);
    const updateSubscription = searchForm.watch(debouncedUpdate);
    return () => updateSubscription.unsubscribe();
  }, [searchForm, setPage]);

  const resultsColumnsDef = useMemo<ColumnDef<UnifiedJobSearchResult>[]>(() => {
    const customerColumns: ColumnDef<UnifiedJobSearchResult>[] = accounting
      ? [
          {
            id: 'customerName',
            accessorFn: (entry) => entry.customerName,
            meta: {
              className: 'w-24',
            },
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Customer Name" column={column} />
            ),
            cell({ row, column }) {
              const value = row.getValue('customerName');
              return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
            },
            enableSorting: false,
          },
          {
            id: 'businessName',
            accessorFn: (entry) => entry.businessName,
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Business Name" column={column} />
            ),
            meta: {
              className: 'w-32',
            },
            cell({ row, column }) {
              const value = row.getValue('businessName');
              return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
            },
            enableSorting: false,
          },
        ]
      : [
          {
            id: 'customer',
            accessorFn: (entry) => {
              const name = entry.customerName || '';
              const business = entry.businessName || '';
              return business ? `${name} - ${business}` : name;
            },
            header: ({ column }) => (
              <DataTableColumnHeader
                hideVisibilityToggle
                title="Customer Name - Business Name"
                column={column}
              />
            ),
            meta: {
              className: 'w-32',
            },
            cell({ row, column }) {
              const value = row.getValue('customer');
              return (
                <div
                  data-column-id="customer"
                  className={cn(
                    column.columnDef.meta?.className,
                    'cursor-pointer text-blue-600 hover:underline',
                  )}
                >
                  {String(value)}
                </div>
              );
            },
            enableSorting: false,
          },
        ];

    const addressColumns: ColumnDef<UnifiedJobSearchResult>[] = accounting
      ? [
          {
            id: 'address',
            accessorFn: (entry) => entry.propertyAddress,
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Address" column={column} />
            ),
            meta: {
              className: 'w-32',
            },
            cell({ row, column }) {
              const value = row.getValue('address');
              return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
            },
            enableSorting: false,
          },
          {
            id: 'city',
            accessorFn: (entry) => entry.propertyCity,
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="City" column={column} />
            ),
            meta: { className: 'w-16' },
            cell({ row, column }) {
              const value = row.getValue('city');
              return <div className={cn(column.columnDef.meta?.className)}>{String(value)}</div>;
            },
            enableSorting: false,
          },
          {
            id: 'zip',
            accessorFn: (entry) => entry.propertyZip,
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Zip" column={column} />
            ),
            meta: { className: 'w-10' },
            cell({ row, column }) {
              const value = row.getValue('zip');
              return <div className={cn(column.columnDef.meta?.className)}>{String(value)}</div>;
            },
            enableSorting: false,
          },
        ]
      : [
          {
            id: 'addressWithCity',
            accessorFn: (entry) => {
              return `${entry.propertyAddress || ''} ${entry.propertyCity || ''} ${entry.propertyZip || ''}`;
            },
            header: ({ column }) => (
              <DataTableColumnHeader
                hideVisibilityToggle
                title="Address City Zip"
                column={column}
              />
            ),
            enableSorting: false,
            meta: {
              className: 'w-32',
            },
            cell({ row, column }) {
              const value = row.getValue('addressWithCity');
              return (
                <div
                  data-column-id="property"
                  className={cn(
                    column.columnDef.meta?.className,
                    'cursor-pointer text-blue-600 hover:underline',
                  )}
                >
                  {String(value)}
                </div>
              );
            },
          },
        ];

    const jobStatusColumn: ColumnDef<UnifiedJobSearchResult>[] = accounting
      ? []
      : [
          {
            id: 'job_status',
            accessorKey: 'jobStatus',
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Job Status" column={column} />
            ),
            meta: {
              className: 'w-32 flex gap-1.5 items-center',
            },
            cell({ row, column }) {
              const result = row.original;
              const statusColor = JOB_STATUS_COLORS[result.jobStatus] || 'CCCCCC';
              return (
                <div className={column.columnDef.meta?.className}>
                  <div
                    className="size-3 rounded-full"
                    style={{ backgroundColor: `#${statusColor}` }}
                    title={result.jobStatus}
                  />
                  <span>{result.jobStatus}</span>
                </div>
              );
            },
          },
          {
            id: 'department',
            accessorFn: (entry) => {
              return entry.departmentId ? departmentMap.get(entry.departmentId) : undefined;
            },
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Department" column={column} />
            ),
            meta: {
              className: 'w-32 break-words',
            },
            cell({ row, column }) {
              const departmentName = row.getValue('department');
              return (
                <div className={column.columnDef.meta?.className}>
                  {departmentName ? String(departmentName) : '-'}
                </div>
              );
            },
            sortingFn: () => 0,
          },
          {
            id: 'project_indicator',
            accessorFn: (entry) => isResultProject(entry),
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Type" column={column} />
            ),
            meta: {
              className: 'w-20',
            },
            cell({ row, column }) {
              const result = row.original;
              return (
                <div className={column.columnDef.meta?.className}>
                  {isResultProject(result) ? (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                      Project
                    </span>
                  ) : (
                    <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-800">
                      Service Call
                    </span>
                  )}
                </div>
              );
            },
            enableSorting: false,
          },
        ];

    const jobTypeColumn: ColumnDef<UnifiedJobSearchResult>[] = accounting
      ? []
      : [
          {
            id: 'job_type',
            accessorFn: (entry) => {
              const jobTypeName = entry.jobTypeId ? jobTypeMap.get(entry.jobTypeId) : undefined;
              const jobSubTypeName = entry.jobSubtypeId
                ? jobSubTypeMap.get(entry.jobSubtypeId)
                : undefined;
              if (jobTypeName && jobSubTypeName) {
                return `${jobTypeName} / ${jobSubTypeName}`;
              }
              return jobTypeName || '-';
            },
            header: ({ column }) => (
              <DataTableColumnHeader
                hideVisibilityToggle
                title="Job Type / Subtype"
                column={column}
              />
            ),
            meta: {
              className: 'w-32 break-words',
            },
            cell({ row, column }) {
              const value = row.getValue('job_type');
              return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
            },
            enableSorting: false,
          },
        ];

    const servicesNeededColumn: ColumnDef<UnifiedJobSearchResult>[] = accounting
      ? []
      : [
          {
            id: 'description',
            accessorKey: 'description',
            header: ({ column }) => (
              <DataTableColumnHeader hideVisibilityToggle title="Description" column={column} />
            ),
            meta: {
              className: 'w-32 break-words',
            },
            cell({ row, column }) {
              const value = row.original.description;
              return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
            },
            enableSorting: false,
          },
        ];

    const actionsColumn: ColumnDef<UnifiedJobSearchResult>[] = onSelectEvent
      ? []
      : [
          {
            id: 'actions',
            header: ({ column }) => (
              <DataTableColumnHeader
                hideVisibilityToggle
                className="mx-auto text-center"
                title="Actions"
                column={column}
              />
            ),
            meta: {
              className: 'mx-auto flex w-40 flex-wrap items-center justify-center gap-1',
            },
            cell({ row, column }) {
              const entry = row.original;

              return (
                <div className={column.columnDef.meta?.className}>
                  <TooltipProvider delayDuration={150}>
                    {userQuery.data?.canViewCostReport && !isResultProject(entry) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon" asChild>
                            <Link
                              to="/cost-report/$jobNumber"
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              params={{ jobNumber: entry.eventId }}
                            >
                              <BarChartIcon />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="px-2 py-1">Job Cost Report</TooltipContent>
                      </Tooltip>
                    )}

                    {entry.customerId > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon" asChild>
                            <Link
                              to="/customers/$customerId"
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              params={{ customerId: entry.customerId }}
                            >
                              <PersonIcon />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="px-2 py-1">View Customer</TooltipContent>
                      </Tooltip>
                    )}

                    {entry.propertyId > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon" asChild>
                            <Link
                              to="/properties/$propertyId"
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              params={{ propertyId: entry.propertyId }}
                            >
                              <HomeIcon />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="px-2 py-1">View Property</TooltipContent>
                      </Tooltip>
                    )}

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="icon" variant="outline" asChild>
                          <Link
                            to={getResultRoute(entry).to}
                            target="_blank"
                            onClick={(e) => e.stopPropagation()}
                            params={getResultRoute(entry).params}
                          >
                            <Pencil1Icon />
                          </Link>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="px-2 py-1">
                        {isResultProject(entry) ? 'Edit Project' : 'Edit Job'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              );
            },
          },
        ];

    const selectColumns: ColumnDef<UnifiedJobSearchResult>[] = onSelectEvent
      ? [
          {
            meta: {
              className: 'w-32',
            },
            id: 'select',
            cell({ row, column }) {
              return (
                <div className={column.columnDef.meta?.className}>
                  <Button
                    data-unified-job-search-select
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectResult(row.original);
                    }}
                  >
                    Select
                  </Button>
                </div>
              );
            },
          },
        ]
      : [];

    const columns: ColumnDef<UnifiedJobSearchResult>[] = [
      ...selectColumns,
      {
        id: 'date_started',
        accessorKey: 'dateStarted',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Date Started" column={column} />
        ),
        meta: {
          className: 'w-20',
        },
        cell({ row, column }) {
          const value = row.original.dateStarted;
          const formatted = value ? formatDate(value) : '-';
          return <div className={column.columnDef.meta?.className}>{formatted}</div>;
        },
        sortingFn: () => 0,
      },
      ...customerColumns,
      ...addressColumns,
      {
        accessorFn: (entry) => {
          return `${entry.jobNumber} / ${entry.poNumber || '-'}`;
        },
        id: 'log_jobNumber',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Job # / PO" column={column} />
        ),
        meta: {
          className: 'w-32 break-all',
        },
        cell({ row, column }) {
          const value = row.getValue('log_jobNumber');
          return <div className={column.columnDef.meta?.className}>{String(value)}</div>;
        },
        sortingFn: () => 0,
      },
      ...jobTypeColumn,
      ...jobStatusColumn,
      ...servicesNeededColumn,
      ...actionsColumn,
    ];
    return columns;
  }, [
    accounting,
    onSelectEvent,
    handleSelectResult,
    userQuery.data?.canViewCostReport,
    departmentMap,
    jobTypeMap,
    jobSubTypeMap,
  ]);

  const skeletonColumns = useMemo<ColumnDef<UnifiedJobSearchResult>[]>(() => {
    return resultsColumnsDef.map((column) => ({
      ...column,
      cell: () => <Skeleton className="bg-foreground/30 h-4 w-32" />,
    }));
  }, [resultsColumnsDef]);

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSort(newSorting[0] ? { id: newSorting[0].id, desc: newSorting[0].desc } : null);
  };

  const isLoading = jobSearchQuery.isPending || jobSearchQuery.isFetching;

  const resultsTable = useReactTable({
    data: isLoading ? loadingResults : jobSearchQuery.data?.results ?? staticArray,
    columns: isLoading ? skeletonColumns : resultsColumnsDef,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    enableRowSelection: true,
    onSortingChange: onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Export functionality
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [exportData, setExportData] = useState<Record<string, string>[]>([]);

  const exportQuery = useUnifiedJobSearchExportQuery({
    filters,
    orderBy: sortParams.orderBy,
    orderDir: sortParams.orderDir,
    enabled: false,
    useV2: true,
  });

  const handleExport = useCallback(async () => {
    setExportStatus('loading');
    try {
      const result = await exportQuery.refetch();
      if (result.data?.results) {
        const exportedData = result.data.results.map((result: UnifiedJobSearchResult) => ({
          'Date Started': result.dateStarted ? formatDate(result.dateStarted) : '-',
          'Customer Name': result.customerName || '',
          'Business Name': result.businessName || '',
          Address: result.propertyAddress || '',
          City: result.propertyCity || '',
          Zip: result.propertyZip || '',
          'Job Number': result.jobNumber,
          'PO Number': result.poNumber || '-',
          'Job Type': result.jobTypeId ? jobTypeMap.get(result.jobTypeId) || '' : '',
          'Job Subtype': result.jobSubtypeId ? jobSubTypeMap.get(result.jobSubtypeId) || '' : '',
          'Job Status': result.jobStatus,
          Description: result.description,
          'Is Project': isResultProject(result) ? 'Yes' : 'No',
          'Project Name': result.projectName || '',
          Department: result.departmentId ? departmentMap.get(result.departmentId) || '' : '',
        }));
        setExportData(exportedData);
        setExportStatus('loaded');
      } else {
        setExportStatus('idle');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      setExportStatus('idle');
    }
  }, [exportQuery, departmentMap, jobTypeMap, jobSubTypeMap]);

  const handleExported = useCallback(() => {
    setExportStatus('idle');
    setExportData([]);
  }, []);

  // On phones Export to Excel is a menu item, which can't keep the export button mounted to
  // download the file, so it downloads here once the rows arrive. Same file, same headers.
  useEffect(() => {
    if (!isPhone || exportStatus !== 'loaded') return;
    downloadJsonAsCsv('unified_job_search_export', exportData);
    handleExported();
  }, [exportData, exportStatus, handleExported, isPhone]);

  const dialogs = (
    <>
      <UnifiedJobCreateDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} />
      {flatRateIsOpen && (
        <Modal
          open
          fullScreen
          onClose={() => setFlatRateIsOpen(false)}
          className="Modal-Remove-Scroll"
        >
          <FlatRateSheet onClose={() => setFlatRateIsOpen(false)} />
        </Modal>
      )}
    </>
  );

  // Phones: the title and Add Job, the rest of the actions under More actions, then search with
  // Filter inside it and the jobs, loading more as the list scrolls. Picker mode keeps Close in
  // sight and leaves out View Flat Rate, as on desktop.
  if (isPhone) {
    const hasMoreActions = !onSelectEvent || showExport;
    return (
      <div className="bg-background min-h-full">
        <main className="mx-auto flex w-full max-w-2xl flex-col gap-3 px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-xl font-bold">Jobs</h1>
            <div className="flex items-center gap-2">
              {onClose && (
                <Button type="button" variant="outline" onClick={onClose}>
                  Close
                </Button>
              )}
              {hasMoreActions && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" disabled={exportStatus === 'loading'}>
                      {exportStatus === 'loading' ? 'Exporting…' : 'More actions'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!onSelectEvent && (
                      <DropdownMenuItem onSelect={() => setFlatRateIsOpen(true)}>
                        View Flat Rate
                      </DropdownMenuItem>
                    )}
                    {showExport && (
                      <DropdownMenuItem onSelect={() => void handleExport()}>
                        Export to Excel
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button type="button" onClick={() => setShowCreateDialog(true)}>
                Add Job
              </Button>
            </div>
          </div>
          <UnifiedJobSearchPhone
            form={searchForm}
            filters={filters}
            sort={sort}
            onSortChange={setSort}
            orderParams={sortParams}
            lookups={{
              departmentName: (id) => departmentMap.get(id),
              jobTypeName: (id) => jobTypeMap.get(id),
              jobSubTypeName: (id) => jobSubTypeMap.get(id),
            }}
            canViewCostReport={userQuery.data?.canViewCostReport}
            onSelectResult={onSelectEvent ? handleSelectResult : undefined}
          />
        </main>
        {dialogs}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold">Jobs</h2>
            {totalEntriesCount !== null && (
              <DataTablePagination
                pageCount={Math.ceil(totalEntriesCount / ROWS_PER_PAGE)}
                currentPage={page}
                setPage={setPage}
              />
            )}
          </div>
          <p className="text-muted-foreground text-sm">Total Entries: {totalEntriesCount ?? 0}</p>
        </div>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 md:flex">
          {onClose && (
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
          {!onSelectEvent && (
            <Button type="button" variant="outline" onClick={() => setFlatRateIsOpen(true)}>
              View Flat Rate
            </Button>
          )}
          <Button
            type="button"
            className="bg-primary hover:bg-primary/90"
            onClick={() => setShowCreateDialog(true)}
          >
            Add Job
          </Button>
        </div>

        {/* Mobile actions dropdown */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 border-gray-300 bg-white">
                <MoreHorizontal className="h-4 w-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onClose && <DropdownMenuItem onClick={onClose}>Close</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setShowCreateDialog(true)}>Add Job</DropdownMenuItem>
              {!onSelectEvent && (
                <DropdownMenuItem onClick={() => setFlatRateIsOpen(true)}>
                  View Flat Rate
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Form {...searchForm}>
        <div className="space-y-4 p-4">
          {/* Global Search Bar */}
          <FormField
            control={searchForm.control}
            name="globalSearch"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                    <Input
                      {...field}
                      className="h-10 rounded-lg border-gray-300 bg-white pl-10"
                      placeholder="Search jobs, customers, addresses... or job #"
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          {/* Filter Form */}
          <UnifiedJobSearchForm />

          {/* Export Button */}
          {showExport && (
            <div className="flex justify-end">
              <ExportJSON
                json={exportData}
                filename="unified_job_search_export"
                onExport={handleExport}
                onExported={handleExported}
                status={exportStatus}
              />
            </div>
          )}
        </div>
      </Form>

      {/* Mobile Card Layout */}
      {isMobile ? (
        <div className="space-y-3 p-4">
          {jobSearchQuery.isPending || jobSearchQuery.isFetching ? (
            <>
              <MobileJobCardSkeleton />
              <MobileJobCardSkeleton />
              <MobileJobCardSkeleton />
              <MobileJobCardSkeleton />
              <MobileJobCardSkeleton />
            </>
          ) : jobSearchQuery.data?.results.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No jobs found</div>
          ) : (
            jobSearchQuery.data?.results.map((result) => (
              <MobileJobCard
                key={result.eventId}
                result={result}
                jobTypeName={result.jobTypeId ? jobTypeMap.get(result.jobTypeId) : undefined}
                jobSubTypeName={
                  result.jobSubtypeId ? jobSubTypeMap.get(result.jobSubtypeId) : undefined
                }
                departmentName={
                  result.departmentId ? departmentMap.get(result.departmentId) : undefined
                }
                canViewCostReport={userQuery.data?.canViewCostReport}
                onSelectResult={onSelectEvent ? handleSelectResult : undefined}
              />
            ))
          )}
        </div>
      ) : (
        /* Desktop DataTable Layout */
        <div className="max-w-[100vw] overflow-x-auto bg-white lg:max-w-full">
          <DataTable
            onRowClick={(result, nativeEvent) => {
              if (jobSearchQuery.data?.results.length) {
                onSelectResultCallback(result, nativeEvent);
              }
            }}
            table={resultsTable}
          />
        </div>
      )}

      {/* Create Job Dialog and Flat Rate Modal */}
      {dialogs}
    </div>
  );
};
